import { useEffect, useMemo, useState } from 'react';
import type {
  Address,
  CatalogResponse,
  CurrentUser,
  Locale,
  PublicProduct,
  VendorPublic,
  ZoneBrief,
} from '@arbuz/shared';
import { api } from '../api';
import { estimateTotal, useCart } from '../cart';
import { formatMoney, formatWeight, makeT } from '../i18n';
import { haptic } from '../tg';
import {
  BottomAction,
  ClientTopBar,
  IconCircle,
  ProductImage,
  QuantityStepper,
  SummaryRow,
} from './clientUi';

/** Каталог отдаётся вместе с зоной: цену доставки назначает тот, кто везёт. */
type CatalogWithZone = CatalogResponse & { zone: ZoneBrief | null };

type Load =
  | { status: 'loading' }
  | { status: 'ready'; data: CatalogWithZone }
  | { status: 'error' };

export function ProductSelect({
  user,
  address,
  vendor,
  onBack,
  onCheckout,
  onChangeAddress,
  onChangeVendor,
}: {
  user: CurrentUser;
  address: Address | null;
  /** Выбранный исполнитель. Без него показывать нечего: каталог его. */
  vendor: VendorPublic | null;
  onBack: () => void;
  onCheckout: () => void;
  onChangeAddress: () => void;
  onChangeVendor: () => void;
}) {
  const locale: Locale = user.locale;
  const t = makeT(locale);
  const cart = useCart();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (!vendor || !address) return;
    let cancelled = false;
    setLoad({ status: 'loading' });

    // Координаты нужны, чтобы сервер вернул зону ЭТОГО исполнителя и её цену:
    // у разных зон одной витрины доставка стоит по-разному.
    api
      .get<CatalogWithZone>(
        `/api/vendors/${vendor.id}/catalog?lat=${address.lat}&lon=${address.lon}`,
      )
      .then((data) => {
        if (!cancelled) setLoad({ status: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [vendor, address]);

  const products = useMemo(() => {
    const map = new Map<number, PublicProduct>();
    if (load.status === 'ready') {
      for (const category of load.data.categories) {
        for (const product of category.products) map.set(product.id, product);
      }
    }
    return map;
  }, [load]);

  const zone = load.status === 'ready' ? load.data.zone : null;
  const knownLines = cart.lines.filter((line) => products.has(line.product_id));
  const missingLines = products.size > 0 ? cart.lines.filter((line) => !products.has(line.product_id)) : [];
  const itemsTotal = estimateTotal(knownLines, products);
  // Доставку считает зона ВЫБРАННОГО исполнителя, а не общая карта площадки
  const delivery = zone ? Number(zone.delivery_price) : Number(vendor?.delivery_price ?? 0);
  const total = itemsTotal + delivery;
  const selectedCount = knownLines.reduce((sum, line) => sum + line.qty, 0);
  const canCheckout = Boolean(vendor && selectedCount > 0 && missingLines.length === 0);

  if (!vendor) {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />
        <section className="client-card client-summary-card">
          <SummaryRow icon="delivery" label={t('vendorLabel')}>
            {t('vendorsText')}
          </SummaryRow>
        </section>
        <BottomAction>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={onChangeVendor}
          >
            {t('vendorsTitle')}
          </button>
        </BottomAction>
      </main>
    );
  }

  if (!address) {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />
        <section className="client-card client-summary-card">
          <SummaryRow icon="location" label={t('addressTitle')}>
            {t('noAddressYet')}
          </SummaryRow>
        </section>
        <BottomAction>
          <button type="button" className="btn-primary" style={{ width: '100%' }} onClick={onChangeAddress}>
            {t('addAddress')}
          </button>
        </BottomAction>
      </main>
    );
  }

  if (load.status === 'loading') {
    return (
      <main className="client-page">
        <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />
        <p className="muted">…</p>
      </main>
    );
  }

  if (load.status === 'error') {
    return (
      <main className="client-page">
        <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />
        <h1 className="h1">{t('loadError')}</h1>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%', marginTop: 'var(--s-4)' }}
          onClick={() => window.location.reload()}
        >
          {t('retry')}
        </button>
      </main>
    );
  }

  const hasProducts = load.data.categories.some((category) => category.products.length > 0);

  // Адрес мог измениться после выбора: исполнитель сюда больше не возит
  if (!zone) {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />
        <section className="client-card client-summary-card">
          <SummaryRow icon="location" label={t('addressTitle')}>
            {address.address_text}
          </SummaryRow>
          <p role="alert" style={{ margin: 'var(--s-4) 0 0', color: 'var(--c-danger)' }}>
            {t('vendorGone')}
          </p>
        </section>
        <BottomAction>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={onChangeVendor}
          >
            {t('vendorChange')}
          </button>
        </BottomAction>
      </main>
    );
  }

  return (
    <main className="client-page with-bottom-bar">
      <ClientTopBar title={t('selectWatermelonTitle')} onBack={onBack} />

      <section className="client-card client-summary-card">
        <SummaryRow icon="location" label={t('availableForAddress')}>
          {address.address_text}
        </SummaryRow>
        <SummaryRow icon="delivery" label={t('vendorLabel')}>
          {vendor.display_name}
          <br />
          {t('deliveryPrice')} {formatMoney(zone.delivery_price, locale)} {t('sum')}
        </SummaryRow>
        <button
          type="button"
          className="client-ghost-button"
          style={{ marginTop: 'var(--s-2)' }}
          onClick={onChangeVendor}
        >
          {t('vendorChange')}
        </button>
      </section>

      <h1 className="h1" style={{ marginTop: 'var(--s-5)' }}>{t('selectWatermelonTitle')}</h1>
      <p className="muted" style={{ margin: 'var(--s-2) 0 var(--s-4)' }}>
        {t('selectWatermelonText')}
      </p>

      {!hasProducts ? (
        <div className="client-card" style={{ padding: 'var(--s-4)' }}>
          <p style={{ margin: 0 }}>{t('emptyCatalog')}</p>
          <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}>
            {t('emptyCatalogHint')}
          </p>
        </div>
      ) : (
        <section style={{ display: 'grid', gap: 'var(--s-3)' }}>
          {load.data.categories.map((category) => (
            <div key={category.id} style={{ display: 'grid', gap: 'var(--s-3)' }}>
              {category.products.map((product) => (
                <AddressProductCard
                  key={product.id}
                  product={product}
                  locale={locale}
                  deliveryPrice={zone.delivery_price}
                  qty={cart.qtyOf(product.id)}
                  onSetQty={(qty) => {
                    haptic('soft');
                    cart.setQty(product.id, qty);
                  }}
                />
              ))}
            </div>
          ))}
        </section>
      )}

      {missingLines.length > 0 && (
        <div className="client-card" role="alert" style={{ padding: 'var(--s-4)', marginTop: 'var(--s-3)' }}>
          <p style={{ margin: 0, color: 'var(--c-danger)' }}>{t('productUnavailable')}</p>
        </div>
      )}

      <BottomAction>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={!canCheckout}
          onClick={() => {
            haptic('light');
            onCheckout();
          }}
        >
          {selectedCount > 0
            ? `${t('checkout')} · ${formatMoney(String(total), locale)} ${t('sum')}`
            : t('selectBeforeCheckout')}
        </button>
      </BottomAction>
    </main>
  );
}

function AddressProductCard({
  product,
  locale,
  deliveryPrice,
  qty,
  onSetQty,
}: {
  product: PublicProduct;
  locale: Locale;
  deliveryPrice: string;
  qty: number;
  onSetQty: (qty: number) => void;
}) {
  const t = makeT(locale);
  const weight = formatWeight(product.weight_min_kg, product.weight_max_kg);

  return (
    <article className="client-card client-product-card">
      <ProductImage src={product.image_url} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)' }}>
              {product.name}
            </h2>
            <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
              {[weight && `${weight} ${t('kg')}`, `${formatMoney(product.price, locale)} ${t('sum')}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <IconCircle icon="delivery" />
        </div>

        <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-xs)' }}>
          {t('deliveryPrice')} {formatMoney(deliveryPrice, locale)} {t('sum')}
        </p>

        {qty > 0 ? (
          <div style={{ marginTop: 'var(--s-3)' }}>
            <QuantityStepper qty={qty} locale={locale} onChange={onSetQty} />
          </div>
        ) : (
          <div className="client-chip-row">
            <button type="button" className="client-chip" onClick={() => onSetQty(1)}>
              1 {t('pcs')}
            </button>
            <button type="button" className="client-chip" onClick={() => onSetQty(2)}>
              2 {t('pcs')}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
